<template>
  <div :class="{[$style.bulk]: isBulkImport}">
    <ImportUsersResultsNav :class="$style.cardsNav" />
    <div
      v-if="!resultsJobId"
    >
      <div
        v-if="!isBulkImport"
        :class="$style.container"
      >
        <table :class="$style.table">
          <thead>
            <tr>
              <th :class="$style.th">
              &nbsp;
              </th>
              <th :class="$style.th">
                First Name
              </th>
              <th :class="$style.th">
                Last Name
              </th>
              <th :class="$style.th">
                Email
              </th>
              <th :class="$style.th">
                Username
              </th>
              <th :class="[$style.th, $style.importMessageColumn]">
                Import results
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="importedUser in getSortedImportedUsersByStatus"
              :key="importedUser.email"
              :class="$style.tbodyTr"
            >
              <td :class="$style.statusColumn">
                <i :class="getImportStatusClass(importedUser.importStatus)" />
              </td>
              <td :class="$style.td">
                {{ importedUser.firstname }}
              </td>
              <td :class="$style.td">
                {{ importedUser.lastname }}
              </td>
              <td :class="$style.td">
                {{ importedUser.email }}
              </td>
              <td :class="$style.td">
                {{ importedUser.username }}
              </td>
              <td :class="getImportMessageClass(importedUser.importStatus)">
                {{ getImportStatusMessage(importedUser) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-else
        :class="$style.container"
      >
        <div
          :class="$style.bulkImportMessage"
        >
          <p>
            While you wait for your import to finish, here are some helpful tips
            making sure your account is <br>set up as best as possible.
            Here are the details for this import.
          </p>
          <p data-test-id="bulkImportDetailsText">
            <strong>Job ID: </strong>
            {{ jobId }}
            <br>
            <strong>Attempting import of: </strong>
            {{ boundItemsTotalCount }} records
          </p>
        </div>
      </div>
    </div>
    <div
      v-else
      :class="$style.bulkImportMessage"
    >
      <p>
        The import has successfully started. An email will be sent with the results once complete.
        <br>
        <strong>Job ID: </strong>
        {{ resultsJobId }}
      </p>
    </div>
  </div>
</template>
<script>
import { ImportUsersConsts, ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { mapGetters } from 'vuex';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ImportUsersResultsNav from 'client/components/ImportUsers/ImportUsersResultsNav.vue';

const { importResultStatus } = ImportUsersConsts;
const { finished, success } = importResultStatus;
const IMPORT_STATUS = 'importStatus';

export default {
  name: 'ImportUsersResults',

  components: {
    ImportUsersResultsNav,
  },

  props: {
    // this is used for Segment event tacking
    importType: {
      type: String,
      required: true,
    },

    itemType: {
      type: String,
      default: ItemTypeConsts.importUsers,
    },

    resultsJobId: {
      type: String,
      default: '',
    },
  },

  computed: {
    ...mapGetters('SettingsModule', {
      hasBilling: 'hasBilling',
    }),

    /**
     * Get array of users sorted by status,
     * 'errored' users come first
     */
    getSortedImportedUsersByStatus() {
      return this.sortByField(this.importedUsers, IMPORT_STATUS);
    },

  },

  beforeCreate() {
    const storeModule = StoreModuleMap[this.$options.propsData.itemType];

    this.$options.computed = {
      ...this.$options.computed,
      ...mapGetters(storeModule, {
        boundItemsTotalCount: 'getBoundItemsTotalCount',
        importedUsers: 'getImportedUsers',
        isBulkImport: 'getIsBulkImport',
        jobId: 'getJobId',
      }),
    };
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    getImportMessageClass(importStatus) {
      const classNames = [
        this.$style.td,
        this.$style.importMessageColumn,
      ];

      if (importStatus === importResultStatus.pending
          || importStatus === importResultStatus.started
          || importStatus === importResultStatus.inProgress) {
        classNames.push(this.$style.loading);
      }

      return classNames;
    },

    getImportStatusClass(importStatus) {
      const classNames = [this.$style.icon];

      switch (importStatus) {
        case importResultStatus.finished:
          classNames.push(this.$style.success, 'jc-actions-check');
          break;

        case importResultStatus.errored:
        case importResultStatus.canceled:
          classNames.push(this.$style.error, 'jc-actions-alert');
          break;

        case importResultStatus.pending:
        case importResultStatus.started:
        case importResultStatus.inProgress:
        default:
          classNames.push('jc-actions-pend');
          break;
      }

      return classNames;
    },

    getImportStatusMessage(importedUser) {
      return importedUser.importMessage !== finished ? importedUser.importMessage : success;
    },

    handleMounted() {
      if (this.isBulkImport) {
        EventHelper.triggerBulkImportUsersInitiated({
          importType: this.importType,
          usersToImport: this.boundItemsTotalCount,
        });
      }
    },

    /**
     * Sort function wrapper that prepares sort field and input array parameters
     * @param {Array} importedUsersArray
     */
    sortByField(importedUsersArray, targetSortField) {
      /**
       * Sort function by a field argument
       * @param {string} field object key to filter on
       * @returns {int} [-1, 0 1]
       *
       * - -1 (user1 comes after user2)
       *
       * - 0 (both users are equal)
       *
       * - 1 (user1 comes before user2)
       */
      const sortBy = (field) => (user1, user2) => {
        const isUser1AfterUser2 = (user1[field] > user2[field]); // 'a' > 'b' = false
        const isUser2AfterUser1 = (user1[field] < user2[field]); // 'a' < 'b' = true
        return isUser1AfterUser2 - isUser2AfterUser1; // -1
      };

      return importedUsersArray.sort(
        sortBy(targetSortField),
      );
    },
  },
};
</script>
<style module>
@value (greyLight2) from '~css/colors.css';

.bulk {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
}

.container {
  height: 100%;
  overflow-x: hidden;
  overflow-y: scroll;
}

.table {
  border-collapse: collapse;
  width: 100%;
}

.th,
.td {
  padding: 15px 7px 10px 6px !important;
  text-align: left !important;
  vertical-align: middle;
}

.th {
  background-color: greyLight2;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.4px;
  position: sticky;
  text-transform: uppercase;
  top: 0;
}

.tbodyTr {
  border-top: 5px solid greyLight2;
}

.td {
  background-color: white;
  font-size: 12px;
}

.statusColumn {
  composes: td;
  text-align: center !important;
}

.importMessageColumn {
  width: 20%;
}

.icon {
  font-size: 16px;
  transition: color 250ms ease-in-out;
  vertical-align: middle;
}

.success {
  color: #80bd01;
}

.error {
  color: #9f0005;
}

.loading::after {
  animation: ellipsis steps(4, end) 900ms infinite;
  content: "\2026"; /* ascii code for the ellipsis character */
  display: inline-block;
  overflow: hidden;
  vertical-align: bottom;
  width: 0;
}

.bulkImportMessage {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  margin: 35px 0 35px 0;
  text-align: center;
}

.bulkImportDetails {
  font-weight: 400;
}

.cardsNav {
  margin-bottom: 16px;
}

@keyframes ellipsis {
  to {
    width: 1.25em;
  }
}
</style>
